import { Asset } from "~/modules/assets/types";
import { getMeasureValue } from "~/utils/getMeasureValue";
import { uniqueValues } from "~/utils/functions/uniqueValues";
import { formatApy } from "~/utils/functions/formatApy";
import {
    FaqItem,
    FrequentlyAskedQuestionSEO,
} from "~/modules/app/seo/FrequentlyAskedQuestionSEO";
import { ASSET_CLASS_IDS } from "~/consts";

// // // //

export function AssetIndividualFaqSEO(props: { asset: Asset }) {
    const { asset } = props;

    const faqItems: Array<FaqItem> = getAssetFaqs({ asset });

    return <FrequentlyAskedQuestionSEO items={faqItems} />;
}

// // // //
// Asset FAQs

// NOTE - this is only designed to support treasury products at the moment
// We'll expand to additional asset classes after we smoke-test OUSG rollout
function getAssetFaqs(props: { asset: Asset }): Array<FaqItem> {
    const { asset } = props;

    if (!asset.ticker) {
        return [];
    }

    const date = new Date();
    const todays_date = `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
    const last_updated = `(last updated ${todays_date})`;

    const items: FaqItem[] = [];

    // Ticker
    if (asset.ticker) {
        items.push({
            question: `What is the ticker of ${asset.name}?`,
            answer: `${asset.ticker}`,
        });
    }

    // Networks
    if (asset.tokens.length) {
        const networkNames = uniqueValues(
            asset.tokens.map((t) => t.network.name),
        );

        items.push({
            question: `What blockchain networks support ${asset.ticker}?`,
            answer: `${asset.ticker} is supported on the following networks: ${networkNames.join(", ")} ${last_updated}.`,
        });
    }

    // Circulating Asset Value
    const total_asset_value_dollar = getMeasureValue(
        asset,
        "total_asset_value_dollar",
    );
    if (total_asset_value_dollar) {
        const total_asset_value = total_asset_value_dollar.val.toLocaleString(
            "en-US",
            { maximumFractionDigits: 2, minimumFractionDigits: 2 },
        );
        items.push({
            question: `What is the total asset value of ${asset.ticker}?`,
            answer: `The total asset value of ${asset.ticker} is $${total_asset_value} ${last_updated}.`,
        });
    }

    // Transfer Volume
    const trailing_30_day_transfer_volume = getMeasureValue(
        asset,
        "trailing_30_day_transfer_volume",
    );
    if (trailing_30_day_transfer_volume) {
        const transfer_volume =
            trailing_30_day_transfer_volume.val.toLocaleString("en-US", {
                maximumFractionDigits: 2,
                minimumFractionDigits: 2,
            });

        items.push({
            question: `What is the 30-day transfer volume of ${asset.ticker}?`,
            answer: `The 30-day transfer volume of ${asset.ticker} is $${transfer_volume} ${last_updated}.`,
        });
    }

    // Monthly Active Addresses
    const trailing_30_day_active_addresses_count = getMeasureValue(
        asset,
        "trailing_30_day_active_addresses_count",
    );
    if (trailing_30_day_active_addresses_count) {
        const active_addresses =
            trailing_30_day_active_addresses_count.val.toLocaleString();
        items.push({
            question: `How many monthly active addresses trade ${asset.ticker}?`,
            answer: `There are ${active_addresses} monthly active addresses for ${asset.ticker} ${last_updated}.`,
        });
    }

    // Monthly Active Addresses
    const holding_addresses_count = getMeasureValue(
        asset,
        "holding_addresses_count",
    );
    if (holding_addresses_count) {
        const holders = holding_addresses_count.val.toLocaleString();
        items.push({
            question: `How many wallets hold ${asset.ticker}?`,
            answer: `There are ${holders} wallets holding ${asset.ticker} ${last_updated}.`,
        });
    }

    // Issuer
    // ENHANCEMENT - add CIK/LEI?
    if (asset.issuer) {
        items.push({
            question: `Who is the issuer of ${asset.ticker}?`,
            answer: `${asset.issuer.legal_name || asset.issuer.name} is the issuer of ${asset.ticker}.`,
        });
    }

    // Platforms
    if (asset.tokens.length) {
        const protocolNames = uniqueValues(
            asset.tokens.map((t) => t.protocol?.name),
        );

        items.push({
            question: `What tokenization platforms support ${asset.ticker}?`,
            answer: `${asset.ticker} is supported on the following tokenization platforms: ${protocolNames.join(", ")} ${last_updated}.`,
        });
    }

    // Get Treasury Product FAQs
    if (asset.asset_class_id === ASSET_CLASS_IDS.TreasuryProduct) {
        // Yield to Maturity
        const yield_to_maturity_percent = getMeasureValue(
            asset,
            "yield_to_maturity_percent",
        );
        if (yield_to_maturity_percent) {
            items.push({
                question: `What is the yield to maturity for ${asset.ticker}?`,
                answer: `The yield to maturity for ${asset.ticker} is ${formatApy(yield_to_maturity_percent.val)}% ${last_updated}.`,
            });
        }

        // Yield to Maturity
        const weighted_average_maturity_years = getMeasureValue(
            asset,
            "weighted_average_maturity_years",
        );
        if (weighted_average_maturity_years) {
            items.push({
                question: `What is the weighted average maturity for ${asset.ticker}?`,
                answer: `The weighted average maturity for ${asset.ticker} is ${weighted_average_maturity_years.val} years ${last_updated}.`,
            });
        }
    }

    return items;
}

// // // //
// NOTES

// - What is the ticker of ${asset.name}?
// - asset.ticker

// What networks support ${asset.name}?
// - ${asset.networks.map((n) => n.name).join(", ")}

// What is ${asset.name}'s protocol?
// - ${asset.protocol_name}

// What is the market cap of ${asset.name}?
// - As of ${todays_date}, the market cap if ${asset.name} is ${asset.circulating_asset_value_dollar}.

// What is the monthly transfer volume of ${asset.name}?
// - As of ${todays_date}, the monthly transfer volume for ${asset.name} is ${asset.monthly_transfer_volume}.

// Who is the issuer of ${asset.name}?
// - ${asset.issuer.name} is the issuer of ${asset.name}.

// What is the yield to maturity of ${asset.name}?
// - The yield to maturity of ${asset.name} is ${asset.yield_to_maturity}.

// What is the weighted average maturity of ${asset.name}?
// - The weighted average maturity of ${asset.name} is ${asset.weighted_avg_maturity}.

// What is the weighted average maturity of ${asset.name}?
// - The weighted average maturity of ${asset.name} is ${asset.weighted_avg_maturity}.

// What is the license or regulatory framework of ${asset.name}?
// - The license or regulatory framework of ${asset.name} is ${asset.reguatory_framework}.

// Who is the custodian of ${asset.name}?
// - ${asset.custodian} is the custodian of ${asset.name}.

// What is the incorporation country of ${asset.name}?
// - ${asset.incorporation_country} is the incorporation country of ${asset.name}.
